import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { CampaignProvider } from '../../contexts/CampaignContext';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { useAppData } from '../../contexts/AppDataContext';
import { AppSidebar } from '../layout/AppSidebar';
import { MobileNav } from '../layout/MobileNav';
import {
  CampaignHeader,
  CampaignStats,
  RecipientsList,
  CampaignChatPanel,
  EmailPreviewPanel,
  EmailEditorModal,
  SettingsModal,
  AddRecipientsModal,
  ChangeConfirmationModal,
  DeleteConfirmModal,
} from './detail';
import SendConfirmationModal from './detail/modals/SendConfirmationModal';
import { useCampaignState, useCampaignEffects, useCampaignHandlers } from './detail/hooks';
import { formatTime, getInitials, getEngagementInsight } from './detail/utils';
import { FilterStatus } from './detail/types';

export const CampaignDetailPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const { openSettings } = useSettingsModal();
  const { refreshCampaigns } = useAppData();

  const state = useCampaignState();
  const { refs } = state;

  useCampaignEffects({
    templateId,
    campaign: state.campaign,
    isLoading: state.isLoading,
    chatHasAnimatedIn: state.chatHasAnimatedIn,
    chatContainerRef: refs.chatContainerRef,
    chatMessages: state.chatMessages,
    filterStatus: state.filterStatus,
    searchQuery: state.searchQuery,
    currentPage: state.currentPage,
    recipientsVersion: state.recipientsVersion,
    setIsLoading: state.setIsLoading,
    setCampaign: state.setCampaign,
    setEditName: state.setEditName,
    setEditSubject: state.setEditSubject,
    setEditBody: state.setEditBody,
    setEditSendWindowStart: state.setEditSendWindowStart,
    setEditSendWindowEnd: state.setEditSendWindowEnd,
    setEditSendDays: state.setEditSendDays,
    setIsChatOpen: state.setIsChatOpen,
    setChatHasAnimatedIn: state.setChatHasAnimatedIn,
    setIsChatLoading: state.setIsChatLoading,
    setChatMessages: state.setChatMessages,
    setTypingText: state.setTypingText,
    setIsTypingComplete: state.setIsTypingComplete,
    setRecipients: state.setRecipients,
    setRecipientsLoading: state.setRecipientsLoading,
    setRecipientsTotal: state.setRecipientsTotal,
    setCurrentPage: state.setCurrentPage,
    setEmailTemplates: state.setEmailTemplates,
    setEmailTemplatesLoading: state.setEmailTemplatesLoading,
  });

  const handlers = useCampaignHandlers({
    templateId,
    campaign: state.campaign,
    editName: state.editName,
    editSubject: state.editSubject,
    editBody: state.editBody,
    editSendWindowStart: state.editSendWindowStart,
    editSendWindowEnd: state.editSendWindowEnd,
    editSendDays: state.editSendDays,
    chatInput: state.chatInput,
    sortField: state.sortField,
    sortDirection: state.sortDirection,
    recipients: state.recipients,
    selectedRecipientIds: state.selectedRecipientIds,
    setCampaign: state.setCampaign,
    setEditName: state.setEditName,
    setIsEditingName: state.setIsEditingName,
    setIsEditingEmail: state.setIsEditingEmail,
    setIsSettingsExpanded: state.setIsSettingsExpanded,
    setShowChangeConfirmation: state.setShowChangeConfirmation,
    setPendingChanges: state.setPendingChanges,
    setChatInput: state.setChatInput,
    setChatMessages: state.setChatMessages,
    setIsChatLoading: state.setIsChatLoading,
    setSortField: state.setSortField,
    setSortDirection: state.setSortDirection,
    setExpandedRecipients: state.setExpandedRecipients,
    setShowSendConfirmation: state.setShowSendConfirmation,
    setIsActivating: state.setIsActivating,
    setRecipients: state.setRecipients,
    setRecipientsTotal: state.setRecipientsTotal,
    setCurrentPage: state.setCurrentPage,
    setSelectedRecipientIds: state.setSelectedRecipientIds,
    setEmailTemplates: state.setEmailTemplates,
    incrementRecipientsVersion: state.incrementRecipientsVersion,
    refreshCampaigns,
  });

  if (state.isLoading || !state.campaign) {
    return (
      <CampaignProvider>
        <div className="h-screen-safe bg-white flex overflow-hidden">
          <MobileNav />
          <div className="flex-1 flex overflow-hidden pt-14 md:pt-0">
            <AppSidebar />
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
            </div>
          </div>
        </div>
      </CampaignProvider>
    );
  }

  const sortedRecipients = handlers.getSortedRecipients();

  // Derive status counts from campaign.stats (not from recipients array)
  // Note: opened/clicked/replied are subsets of sent, so don't add them for 'all' count
  const campaignStats = state.campaign.stats;
  const statusCounts: Record<FilterStatus, number> = {
    all: campaignStats.draft + campaignStats.queued + campaignStats.sent + campaignStats.bounced,
    draft: campaignStats.draft,
    queued: campaignStats.queued,
    sent: campaignStats.sent,
    opened: campaignStats.opened,
    clicked: campaignStats.clicked,
    replied: campaignStats.replied,
    bounced: campaignStats.bounced,
  };

  return (
    <CampaignProvider>
      <div className="h-screen-safe bg-white flex overflow-hidden relative">
        <MobileNav />
        <div className="flex-1 flex overflow-hidden pt-14 md:pt-0">
          <AppSidebar />

          <style>{`
            @media (min-width: 640px) {
              .main-content-chat-adjust { margin-right: ${state.isChatOpen ? '380px' : '0px'}; }
            }
          `}</style>
          <div
            className="main-content-chat-adjust flex-1 flex flex-col min-w-0 overflow-hidden bg-white"
            style={{
              transition: 'margin-right 500ms cubic-bezier(0.16, 1, 0.3, 1)',
              minWidth: 0
            }}
          >
            <CampaignHeader
              campaign={state.campaign}
              isEditingName={state.isEditingName}
              editName={state.editName}
              showMoreMenu={state.showMoreMenu}
              onEditNameChange={state.setEditName}
              onStartEditName={() => state.setIsEditingName(true)}
              onSaveName={handlers.handleSaveName}
              onCancelNameEdit={handlers.handleCancelNameEdit}
              onToggleStatus={handlers.toggleStatus}
              onToggleMoreMenu={() => state.setShowMoreMenu(!state.showMoreMenu)}
              onDelete={() => { state.setShowDeleteConfirm(true); state.setShowMoreMenu(false); }}
            />

            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden lg:border-r border-neutral-100">
                <CampaignStats
                  campaign={state.campaign}
                  filterStatus={state.filterStatus}
                  onFilterChange={state.setFilterStatus}
                />

                <RecipientsList
                  recipients={sortedRecipients}
                  totalCount={state.recipientsTotal}
                  statusCounts={statusCounts}
                  filterStatus={state.filterStatus}
                  searchQuery={state.searchQuery}
                  isSearchExpanded={state.isSearchExpanded}
                  sortField={state.sortField}
                  sortDirection={state.sortDirection}
                  expandedRecipients={state.expandedRecipients}
                  hoveredEngagement={state.hoveredEngagement}
                  showFilterMenu={state.showFilterMenu}
                  searchInputRef={refs.searchInputRef}
                  isLoadingRecipients={state.recipientsLoading}
                  currentPage={state.currentPage}
                  selectedIds={state.selectedRecipientIds}
                  campaignDatasets={state.campaign.datasets}
                  onFilterChange={state.setFilterStatus}
                  onSearchChange={state.setSearchQuery}
                  onToggleSearchExpanded={() => state.setIsSearchExpanded(!state.isSearchExpanded)}
                  onSort={handlers.handleSort}
                  onToggleExpanded={handlers.handleToggleExpanded}
                  onHoverEngagement={state.setHoveredEngagement}
                  onToggleFilterMenu={() => state.setShowFilterMenu(!state.showFilterMenu)}
                  onAddRecipients={() => state.setShowAddRecipientsModal(true)}
                  onRemoveRecipient={handlers.handleRemoveRecipient}
                  onPageChange={handlers.handlePageChange}
                  onToggleSelection={handlers.handleToggleRecipientSelection}
                  onToggleSelectAll={handlers.handleToggleSelectAll}
                  onBatchRemove={handlers.handleBatchRemove}
                  getEngagementInsight={getEngagementInsight}
                  formatTime={formatTime}
                  getInitials={getInitials}
                />
              </div>

              <EmailPreviewPanel
                campaign={state.campaign}
                isChatOpen={state.isChatOpen}
                onEditEmail={() => state.setIsEditingEmail(true)}
              />
            </div>
          </div>

          <CampaignChatPanel
            isOpen={state.isChatOpen}
            campaign={state.campaign}
            chatMessages={state.chatMessages}
            chatInput={state.chatInput}
            isChatLoading={state.isChatLoading}
            isTypingComplete={state.isTypingComplete}
            typingText={state.typingText}
            chatContainerRef={refs.chatContainerRef}
            chatInputRef={refs.chatInputRef}
            onClose={() => state.setIsChatOpen(false)}
            onOpen={() => state.setIsChatOpen(true)}
            onInputChange={state.setChatInput}
            onSend={handlers.handleChatSend}
            onEditEmail={() => state.setIsEditingEmail(true)}
            onEditSettings={() => state.setIsSettingsExpanded(true)}
          />
        </div>

        <EmailEditorModal
          isOpen={state.isEditingEmail}
          campaign={state.campaign}
          editSubject={state.editSubject}
          editBody={state.editBody}
          selectedTemplateId={state.selectedTemplateId}
          templates={state.emailTemplates}
          templatesLoading={state.emailTemplatesLoading}
          onClose={() => state.setIsEditingEmail(false)}
          onSubjectChange={state.setEditSubject}
          onBodyChange={(content) => state.setEditBody(content)}
          onTemplateSelect={(template) => state.setSelectedTemplateId(template?.id || null)}
          onSave={handlers.handleSaveEmail}
          onSaveAsTemplate={handlers.handleSaveAsTemplate}
        />

        <SettingsModal
          isOpen={state.isSettingsExpanded}
          emailAccounts={[{
            id: 'current',
            email: state.campaign.senderEmail,
            name: state.campaign.senderEmail.split('@')[0],
            provider: 'gmail'
          }]}
          editSendWindowStart={state.editSendWindowStart}
          editSendWindowEnd={state.editSendWindowEnd}
          editSendDays={state.editSendDays}
          selectedEmailAccount={state.selectedEmailAccount}
          selectedTimezone={state.selectedTimezone}
          onClose={() => state.setIsSettingsExpanded(false)}
          onSendWindowStartChange={state.setEditSendWindowStart}
          onSendWindowEndChange={state.setEditSendWindowEnd}
          onEmailAccountChange={state.setSelectedEmailAccount}
          onTimezoneChange={state.setSelectedTimezone}
          onSave={handlers.handleSaveSettings}
          onOpenEmailSettings={() => openSettings('email')}
        />

        <AddRecipientsModal
          isOpen={state.showAddRecipientsModal}
          campaign={state.campaign}
          onClose={() => state.setShowAddRecipientsModal(false)}
          onRecipientsAdded={handlers.handleRecipientsAdded}
        />

        <ChangeConfirmationModal
          isOpen={state.showChangeConfirmation}
          pendingChanges={state.pendingChanges}
          onClose={() => {
            state.setShowChangeConfirmation(false);
            state.setPendingChanges(null);
          }}
          onApplyChanges={(restart) => handlers.applyChanges(state.pendingChanges, restart)}
        />

        <DeleteConfirmModal
          isOpen={state.showDeleteConfirm}
          campaignName={state.campaign.name}
          onClose={() => state.setShowDeleteConfirm(false)}
          onDelete={handlers.handleDelete}
        />

        <SendConfirmationModal
          isOpen={state.showSendConfirmation}
          onClose={() => state.setShowSendConfirmation(false)}
          onConfirm={handlers.confirmActivation}
          isLoading={state.isActivating}
          campaignName={state.campaign.name}
          recipientCount={statusCounts.all}
          senderEmail={state.campaign.senderEmail}
          emailSubject={state.campaign.subject}
        />
      </div>
    </CampaignProvider>
  );
};

export default CampaignDetailPage;
